import { Trash2, Plus, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import type { Position } from '../lib/typen';
import { bereinigeInitialen, initialenAusName, INITIALEN_MAX, leerePosition, nummeriere } from '../lib/liste';

const GROESSEN = ['116', '128', '140', '152', '164', '176', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

interface Props {
  positionen: Position[];
  onChange: (p: Position[]) => void;
}

const zelle = 'w-full rounded border border-transparent bg-transparent px-1.5 py-1 text-sm hover:border-[#DAD9D9] focus:border-[#1A1A1A] focus:bg-white focus:outline-none';

export default function PositionenTabelle({ positionen, onChange }: Props) {
  const setze = (i: number, patch: Partial<Position>) => {
    const neu = positionen.slice();
    const alt = neu[i];
    const p = { ...alt, ...patch };
    // Initialen automatisch nachziehen, solange sie noch zum Namen passen (oder leer sind)
    if (('vorname' in patch || 'nachname' in patch) && (!alt.initialen || alt.initialen === initialenAusName(alt.vorname, alt.nachname))) {
      p.initialen = initialenAusName(p.vorname, p.nachname);
    }
    neu[i] = p;
    onChange(neu);
  };

  return (
    <div className="rounded-lg border border-[#DAD9D9] bg-white">
      <datalist id="druck-initialen-groessen">
        {GROESSEN.map((g) => <option key={g} value={g} />)}
      </datalist>
      <table className="w-full text-sm">
        <thead className="bg-[#1A1A1A] text-left text-white">
          <tr>
            <th className="w-10 px-2 py-2">#</th>
            <th className="px-2 py-2">Vorname</th>
            <th className="px-2 py-2">Nachname</th>
            <th className="w-28 px-2 py-2">Initialen</th>
            <th className="w-24 px-2 py-2">Größe</th>
            <th className="w-16 px-2 py-2">Menge</th>
            <th className="px-2 py-2">Anmerkung</th>
            <th className="w-20 px-2 py-2 text-center">Erledigt</th>
            <th className="w-16 px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {positionen.map((p, i) => (
            <tr key={i} className={`border-t border-[#DAD9D9] ${p.unsicher ? 'bg-amber-50' : ''} ${p.erledigt ? 'opacity-60' : ''}`}>
              <td className="px-2 py-1 text-gray-500 tabular-nums">{p.nr}</td>
              <td className="px-1 py-1"><input className={zelle} value={p.vorname} onChange={(e) => setze(i, { vorname: e.target.value })} /></td>
              <td className="px-1 py-1"><input className={zelle} value={p.nachname} onChange={(e) => setze(i, { nachname: e.target.value })} /></td>
              <td className="px-1 py-1">
                <input
                  className="w-full rounded bg-[#1A1A1A] px-2 py-1 text-center text-sm font-bold uppercase tracking-wide text-white focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
                  value={p.initialen}
                  maxLength={INITIALEN_MAX}
                  onChange={(e) => setze(i, { initialen: bereinigeInitialen(e.target.value) })}
                />
              </td>
              <td className="px-1 py-1">
                <input
                  list="druck-initialen-groessen"
                  className="w-full rounded bg-[#1e3a8a] px-2 py-1 text-center text-sm font-semibold uppercase text-white focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
                  value={p.groesse === 'OHNE' ? '' : p.groesse}
                  onChange={(e) => setze(i, { groesse: e.target.value.toUpperCase().trim() })}
                />
              </td>
              <td className="px-1 py-1">
                <input type="number" min={1} max={99} className={zelle} value={p.menge}
                  onChange={(e) => setze(i, { menge: Math.max(1, Number(e.target.value) || 1) })} />
              </td>
              <td className="px-1 py-1">
                <div className="flex items-center gap-1">
                  {p.unsicher && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-label="unsicher gelesen" />}
                  <input className={zelle} value={p.anmerkung} onChange={(e) => setze(i, { anmerkung: e.target.value })} />
                </div>
              </td>
              <td className="px-2 py-1 text-center">
                <input type="checkbox" className="h-4 w-4 accent-[#1A1A1A]" checked={p.erledigt} onChange={(e) => setze(i, { erledigt: e.target.checked })} />
              </td>
              <td className="px-1 py-1">
                <div className="flex justify-end gap-1">
                  {p.unsicher && (
                    <button title="Geprüft — Markierung entfernen" className="rounded p-1 text-amber-600 hover:bg-amber-100" onClick={() => setze(i, { unsicher: false })}>
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button title="Zeile löschen" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() => onChange(nummeriere(positionen.filter((_, j) => j !== i)))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-[#DAD9D9] p-2">
        <Button variant="ghost" size="sm" onClick={() => onChange([...positionen, leerePosition(positionen.length + 1)])}>
          <Plus className="mr-1 h-4 w-4" /> Zeile hinzufügen
        </Button>
      </div>
    </div>
  );
}
